import { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { ModalPopup, ModalbuttonWrap, Modalbutton, 
	ModalbuttonNo, DialogTextArea, DialogContentInfo, ModalORText } from './style';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { toggleDebugDialogState } from 'store/slices/DebugDialogSlice';
import { useLocation } from 'react-router-dom';
import { downloadDataAsJSONFile } from '../util';
import { sendDebugData } from '../ButtonDiv/api';
import { CloseBtn } from '../EditFacetModal/style';

type SystemLossesData = {
  baseSystemLosses: number;
  systemLosses: number;
  totalSystemLosses: number;
}

type ProposalData = {
  proposalUrl: string;
  createdAt: string;
  updatedAt: string;
  systemLossesData: SystemLossesData;
}

type panelDetailsType = {
    wattage: number;
    key: string;
    height: number;
    width: number;
    panelName: string;
}

type debugDataType = {
	tool: string;
	proposalId: string;
	uuid: string;
	mode: string;
	isSunroofAvailable: boolean;
	imageryQuality: string|undefined;
	lat: string;
	lng: string;
	imagerySource: string;
	selectedPanelDetails: panelDetailsType;
	jpgUrl: string;
	dsmUrl: string|undefined;
	allRoofSegs: RasterRoofSegment[];
	designUrl: string;
	energyDerateRate: number;
	derateRateBeforeUserSwitchToNonDefaultModes: number;
	debugDataCapturedAt: string;
	designCreatedAt: string;
	designUpdatedAt: string;
	imageryCapturedDate: Date|null;
	solarDataCapturedDate: Date|null;
	comment?: string;
	screenshotImage?: string | null;
	errorInCapturingScreenshot?: string | null;
	specialEdges?: SpecialEdges[];
	proposalCreatedAt: Date | null;
	proposalUpdatedAt: Date | null;
	systemLossesData: SystemLossesData;
	shadingAnalysis: Record<string, unknown>;
};

export default function DebugDialog() {
	const { open, screenshotImage, errorInCapturingScreenshot } = useSelector((state: RootState) => state.debugDialog.data);
	const { uuid, externalId, mode, isSunroofAvailable, latLng, imagerySource, jpgUrl, dsmUrl, allRoofSegs,
		createdAt, updatedAt, panel:selectedPanelDetails, imageryQuality, imageryCapturedDate, solarDataCapturedDate, designType, specialEdges, shadingAnalysis } =
		useSelector((state: RootState) => state.roofData.data);
	const { energyDerateRateFromParent } = useSelector((state: RootState) => state.QueryParams.data);
	const location = useLocation();
	const dispatch = useDispatch();
	const [comment, setComment] = useState<string>('');
	const [proposalData, setProposalData] = useState<null | ProposalData>(null);
	const isRoofDesign = designType === 'ROOF';

	useEffect(() => {
		function handleReceiveProposalUrl(event: MessageEvent) {
			if (event.data.id === 'PROPOSAL_URL_SENT') {
				console.log('e: ', event.data);
				setProposalData({
					proposalUrl: event.data.detail.proposalUrl,
					createdAt: event.data.detail.created_at,
					updatedAt: event.data.detail.updated_at,
					systemLossesData: {
						baseSystemLosses: event.data.detail.system_losses_data.base_system_losses,
						systemLosses: event.data.detail.system_losses_data.system_losses,
						totalSystemLosses: event.data.detail.system_losses_data.total_system_losses,
					}
				});
			}
		}
		window.addEventListener('message', handleReceiveProposalUrl);
		return () => {
			window.removeEventListener('message', handleReceiveProposalUrl);
		};
	});

	function getDebugData(debugDataForDev = false){
		const roofsegs = allRoofSegs.map((roofSeg) => {
			const { hullCoords, panels, ...rest } = roofSeg;
			let panelWithoutExtCoords;
			if (!isRoofDesign) {
				panelWithoutExtCoords = panels.map((panel) => {
					const { exteriorCoords, ...rest } = panel;
					return { ...rest };
				});
			}
			return {
				...rest,
				...(isRoofDesign ? {} : { panels: panelWithoutExtCoords }),
			};
		});

		let designUrl = '';
		try{
			designUrl =`${window.location.origin}${location.pathname}${location.search}`;
			const url = new URL(designUrl);
			const newToken = localStorage.getItem(`roleHash-${externalId}`);
			if(newToken){
				url.searchParams.set('role', newToken);
			}
			designUrl = url.toString().replace('show-data', '');
		}
		catch(ex){
			console.log('err:', ex);
		}
		const [lat, lng] = latLng.split(',');

		// Drop fields already captured elsewhere in debugData (lat/lng, timestamps).
		const { createdAt: shadingCreatedAt, updatedAt: shadingUpdatedAt, latitude, longitude, monthlyFluxUrl, nsrdbGid, ...shadingAnalysisForDebug } =
			shadingAnalysis as Record<string, unknown>;

		const debugData: Partial<debugDataType> = {};

		if (debugDataForDev) {
			debugData['comment'] = comment;
			debugData['screenshotImage'] = screenshotImage;
			debugData['errorInCapturingScreenshot'] = errorInCapturingScreenshot;
		}
		Object.assign(debugData, {
			tool: 'AI tool',
			proposalId: externalId,
			uuid,
			mode,
			isSunroofAvailable,
			imageryQuality,
			lat,
			lng,
			imagerySource,
			jpgUrl,
			dsmUrl,
			designUrl,
			proposalUrl: proposalData?.proposalUrl,
			energyDerateRate: energyDerateRateFromParent,
			debugDataCapturedAt: new Date().toISOString(),
			designCreatedAt: createdAt,
			designUpdatedAt: updatedAt,
			imageryCapturedDate,
			solarDataCapturedDate,
			proposalCreatedAt: proposalData?.createdAt,
			proposalUpdatedAt: proposalData?.updatedAt,
			selectedPanelDetails,
			allRoofSegs: roofsegs,
			systemLossesData: proposalData?.systemLossesData,
			specialEdges: isRoofDesign ? specialEdges : [],
			shadingAnalysis: shadingAnalysisForDebug,
		});
		return debugData;
	}
	
	function sendDebugDataToDev(){
		const debugData = getDebugData(true);
		sendDebugData(JSON.stringify(debugData));
		setComment('');
		dispatch(toggleDebugDialogState({ value: false }));
	}

	function downloadDebugData(){
		const debugData = getDebugData();
		downloadDataAsJSONFile(debugData, `debug_data_${externalId}`);
		setComment('');
		dispatch(toggleDebugDialogState({ value: false }));
	}

	return (
		<Popup
			contentStyle={{ 
				width: '96%', maxWidth: '580px', 
				borderRadius: '4px', margin: '120px auto auto',
				background: 'var(--modalBgColor)', borderColor: 'var(--modalBorderColor)' }}
			open={open}
			closeOnDocumentClick={false}
			onClose={() => {
				if (open)
					dispatch(toggleDebugDialogState({ value: false }));
			}}>
			<CloseBtn>
				<svg
					onClick={() => {
						setComment('');
						dispatch(toggleDebugDialogState({ value: false }));
					}}
					xmlns='http://www.w3.org/2000/svg'
					fill='none'
					viewBox='0 0 24 24'
					stroke='var(--defaultIconColor)'
					strokeWidth='2'
				>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						d='M6 18L18 6M6 6l12 12'
					></path>
				</svg>
			</CloseBtn>
			<ModalPopup>
				<DialogContentInfo>
					Explain the issue <strong>(Optional)</strong>:
				</DialogContentInfo>
				<DialogTextArea value={comment} onChange={e => setComment(e.target.value)}/>

				<ModalbuttonWrap>
					
					<Modalbutton onClick={sendDebugDataToDev}>
						Send debug data to Dev Team
						<svg xmlns="http://www.w3.org/2000/svg" width="31" height="19" viewBox="0 0 55 34" fill="none">
							<path d="M37.4258 22.5413C39.1571 20.9254 41.5052 20.0176 43.9537 20.0176C46.4021 20.0176 48.7503 20.9254 50.4816 22.5413C52.2129 24.1571 53.1855 26.3488 53.1855 28.634H40.8764M49.229 11.4012C49.229 12.707 48.6732 13.9594 47.6839 14.8827C46.6946 15.8061 45.3528 16.3248 43.9537 16.3248C42.5546 16.3248 41.2128 15.8061 40.2235 14.8827C39.2341 13.9594 38.6783 12.707 38.6783 11.4012C38.6783 10.0954 39.2341 8.84301 40.2235 7.91964C41.2128 6.99628 42.5546 6.47754 43.9537 6.47754C45.3528 6.47754 46.6946 6.99628 47.6839 7.91964C48.6732 8.84301 49.229 10.0954 49.229 11.4012Z" 
								stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M17.7597 22.5413C16.0284 20.9254 13.6803 20.0176 11.2318 20.0176C8.78341 20.0176 6.43525 20.9254 4.70394 22.5413C2.97264 24.1571 2 26.3488 2 28.634H14.3091M16.5072 11.4012C16.5072 12.707 15.9514 13.9594 14.9621 14.8827C13.9728 15.8061 12.631 16.3248 11.2318 16.3248C9.83274 16.3248 8.49093 15.8061 7.50162 14.8827C6.5123 13.9594 5.9565 12.707 5.9565 11.4012C5.9565 10.0954 6.5123 8.84301 7.50162 7.91964C8.49093 6.99628 9.83274 6.47754 11.2318 6.47754C12.631 6.47754 13.9728 6.99628 14.9621 7.91964C15.9514 8.84301 16.5072 10.0954 16.5072 11.4012Z" 
								stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M35.1651 8.4829C35.1651 10.2738 34.4029 11.9913 33.0461 13.2576C31.6893 14.5239 29.8491 15.2353 27.9303 15.2353C26.0116 15.2353 24.1714 14.5239 22.8146 13.2576C21.4578 11.9913 20.6956 10.2738 20.6956 8.4829C20.6956 6.69205 21.4578 4.97454 22.8146 3.70821C24.1714 2.44188 26.0116 1.73047 27.9303 1.73047C29.8491 1.73047 31.6893 2.44188 33.0461 3.70821C34.4029 4.97454 35.1651 6.69205 35.1651 8.4829ZM27.9303 20.2997C24.5725 20.2997 21.3522 21.5446 18.9778 23.7607C16.6034 25.9768 15.2695 28.9824 15.2695 32.1164H40.5912C40.5912 28.9824 39.2573 25.9768 36.8829 23.7607C34.5085 21.5446 31.2882 20.2997 27.9303 20.2997Z" 
								stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
					</Modalbutton>

					<ModalORText><span>OR</span></ModalORText>

					<ModalbuttonNo onClick={downloadDebugData}>
						Download Data 
						<svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 48 46" fill="none">
							<path d="M13.7891 27.0312L23.9065 35.7424L33.9761 26.976" stroke="var(--textTitleColor)" 
								strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M23.8242 2L23.9135 34.579" stroke="var(--textTitleColor)" 
								strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M2 35.7793V41.7793C2 42.8839 2.89543 43.7793 4 43.7793H44C45.1046 43.7793 46 42.8839 46 41.7793V35.7793" 
								stroke="var(--textTitleColor)" strokeWidth="4" strokeLinecap="round"/>
						</svg>
					</ModalbuttonNo>
				</ModalbuttonWrap>
			</ModalPopup>
		</Popup>
	);
}